import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionTitle from "@/components/base/SectionTitle";
import { faqs, siteStats, socialLinks, teamMembers } from "@/mocks/site";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: "关于我们 - 国产自拍国产自制影视内容平台",
    description:
      "了解国产自拍平台的定位、运营团队与联系方式，查看常见问题与使用说明。",
    keywords: "国产自拍,关于我们,国产影视平台,联系国产自拍",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />

      <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-primary-500 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
          <i className="ri-quill-pen-line" />
          品牌介绍
        </span>
        <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
          国产自拍 · 国产自制影视内容平台
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-100 md:text-base">
          国产自拍是一个专注于国产自制影视作品的内容展示与资讯平台。我们聚焦电影、电视剧与综艺三大板块，
          持续整理最新上线的国产原创内容，为影迷提供便捷的浏览、筛选与查询体验。
          无论是热播自制剧还是小众原创影片，我们希望每一部用心创作的中文影视作品都能被更多人看见。
        </p>
        <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {siteStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg border border-primary-400 bg-primary-600 p-4 md:p-5"
            >
              <p className="font-heading text-xl font-black text-secondary-400 md:text-2xl">
                {stat.value}
              </p>
              <p className="mt-1 text-xs text-primary-100 md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionTitle title="运营团队" subtitle="一群热爱国产影视内容的伙伴" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {teamMembers.map((member) => (
            <div
              key={member.name}
              className="rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-accent-300 hover:bg-background-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 font-heading text-xl font-bold text-secondary-800">
                {member.name.slice(0, 1)}
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {member.name}
              </h3>
              <p className="mt-0.5 text-xs font-medium text-accent-600">
                {member.role}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {member.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionTitle title="联系我们" subtitle="欢迎交流内容合作与使用反馈" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <button
            type="button"
            onClick={() => showToast("邮箱：hello@gczp-film.demo（前端演示）")}
            className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 text-left transition-colors hover:border-accent-300"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-mail-line text-xl" />
            </span>
            <span>
              <span className="block text-sm font-bold text-foreground-950">商务邮箱</span>
              <span className="block text-sm text-foreground-600">hello@gczp-film.demo</span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => showToast("地址：北京市朝阳区影视产业园（前端演示）")}
            className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 text-left transition-colors hover:border-accent-300"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-map-pin-line text-xl" />
            </span>
            <span>
              <span className="block text-sm font-bold text-foreground-950">办公地址</span>
              <span className="block text-sm text-foreground-600">
                北京市朝阳区影视产业园
              </span>
            </span>
          </button>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-share-circle-line text-xl" />
            </span>
            <p className="mt-3 text-sm font-bold text-foreground-950">关注我们</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {socialLinks.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => showToast(`${item.label}：${item.handle}（前端演示）`)}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <SectionTitle title="常见问题" subtitle="关于平台使用的高频疑问" />
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {faqs.map((faq, index) => {
            const open = openFaq === index;
            return (
              <div key={faq.q} className="border-b border-background-200 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6"
                >
                  <span className="flex items-center gap-3 text-sm font-medium text-foreground-900 md:text-base">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-xs font-bold text-secondary-800">
                      Q
                    </span>
                    {faq.q}
                  </span>
                  <i
                    className={`${
                      open ? "ri-subtract-line" : "ri-add-line"
                    } text-lg text-accent-600`}
                  />
                </button>
                {open && (
                  <p className="px-5 pb-5 pl-14 text-sm leading-relaxed text-foreground-600 md:px-6 md:pl-16">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="mt-10 flex justify-center">
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line text-lg" />
          返回首页
        </Link>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fadeUp rounded-md bg-foreground-950/90 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}